import {
  Alert,
  Button,
  EmptyState,
  Field,
  PageHeader,
  Skeleton,
  StatusBadge,
  todayInArgentina,
} from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { PackageCheck } from 'lucide-react';
import type { JSX } from 'react';

import { QueueItemStatus } from '../../deliveries/queue-status';
import { useSignatureQueue } from '../../deliveries/use-signature-queue';
import { loadDeliveries } from '../../deliveries/deliveries-repository';
import { addDays, formatClock, formatSiteAddress, itemsLabel } from '../../deliveries/delivery-format';
import type { DeliveryNote } from '../../delivery-notes/api';
import type { QueuedSignature } from '../../deliveries/signature-queue';

const myDeliveriesRoute = getRouteApi('/protected/entregas');

/**
 * `offline-signature` S5: phone-first list of the notes to deliver on a date
 * (DISPATCHED / DELIVERED). The date lives in the URL like every list in the app.
 */
export function MyDeliveriesPage(): JSX.Element {
  const search = myDeliveriesRoute.useSearch();
  const navigate = myDeliveriesRoute.useNavigate();
  const today = todayInArgentina();
  const tomorrow = addDays(today, 1);
  const date = search.fecha ?? today;

  const query = useQuery({
    queryKey: ['deliveries', 'list', date],
    queryFn: () => loadDeliveries(date),
  });

  const queue = useSignatureQueue();
  const hasPending = queue.items.some((item) => item.state === 'pending');

  const setDate = (fecha: string): void => {
    void navigate({ search: { fecha } });
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Mis entregas" />

      <div className="flex flex-wrap items-end gap-2">
        <Button variant={date === today ? 'primary' : 'secondary'} onClick={() => setDate(today)}>
          Hoy
        </Button>
        <Button variant={date === tomorrow ? 'primary' : 'secondary'} onClick={() => setDate(tomorrow)}>
          Mañana
        </Button>
        <Field
          label="Fecha"
          name="fecha"
          type="date"
          value={date}
          onChange={(event) => {
            if (event.target.value !== '') {
              setDate(event.target.value);
            }
          }}
        />
      </div>

      {query.isPending ? <Skeleton className="h-24 w-full" /> : null}
      {query.isError ? <Alert tone="danger">No pudimos cargar tus entregas. Intentá de nuevo. Si no tenés señal, abrí antes esta fecha con conexión.</Alert> : null}

      {queue.authRequired ? (
        <Alert tone="warning">Iniciá sesión de nuevo para enviar las firmas que quedaron guardadas.</Alert>
      ) : null}
      {hasPending ? (
        <Button variant="secondary" loading={queue.syncing} onClick={() => void queue.syncNow()}>
          Enviar ahora
        </Button>
      ) : null}

      {query.data !== undefined ? (
        <>
          <p className="text-sm text-slate-600">
            {query.data.fromCache
              ? `Sin conexión — mostrando lo último que cargaste a las ${formatClock(query.data.fetchedAt)}`
              : `Datos actualizados a las ${formatClock(query.data.fetchedAt)}`}
          </p>
          {query.data.data.length === 0 ? (
            <EmptyState icon={PackageCheck} title="No hay entregas para esta fecha" />
          ) : (
            <ul className="flex flex-col gap-3">
              {query.data.data.map((note) => (
                <li key={note.id}>
                  <DeliveryCard note={note} />
                  {queue.byNote.get(note.id) !== undefined ? (
                    <div className="mt-2">
                      <QueueItemStatus
                        item={queue.byNote.get(note.id) as QueuedSignature}
                        onDismiss={(noteId) => void queue.dismiss(noteId)}
                      />
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}

function DeliveryCard({ note }: { note: DeliveryNote }): JSX.Element {
  const address = formatSiteAddress(note);

  return (
    <Link
      to="/entregas/$id"
      params={{ id: note.id }}
      className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm active:bg-slate-50"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-900">{note.noteNumber}</span>
        <StatusBadge domain="deliverynote" status={note.status} />
      </span>
      <span className="text-slate-900">{note.customerNameSnapshot}</span>
      <span className="text-sm text-slate-600">{note.siteNameSnapshot}</span>
      {address !== null ? <span className="text-sm text-slate-600">{address}</span> : null}
      <span className="text-sm text-slate-600">{itemsLabel(note.items.length)}</span>
    </Link>
  );
}
